import clsx from 'clsx';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Field, Flex, Input, Separator, Switch, Textarea } from '@optiaxiom/react';

import { PageHeader } from '@frontend/components';
import { useNamespacedNavigate } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';

import { useInstructionReducer } from '../hooks/useInstructionReducer';
import { useUserInstruction } from '../hooks/useUserInstruction';
import { UserInstruction } from '../types';

import { styles } from './InstructionForm.css';
import UserInstructionProductScope from './UserInstructionProductScope';

export function InstructionForm() {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const instructionToEdit = location.state as UserInstruction;
  const [formData, dispatch] = useInstructionReducer(instructionToEdit);

  const instructionType = useMemo(() => {
    return location.pathname.includes('system-instruction') ? 'system' : 'user';
  }, [location.pathname]);

  const { removeInstruction, createNewInstruction, updateInstruction } = useUserInstruction({
    instructionType: instructionType,
  });

  const handleNavigation = () => {
    if (instructionType === 'system') {
      navigate('/instructions/system');
    } else {
      navigate('/instructions');
    }
  };
  const handleDeleteInstruction = (instructionGuid: string) => {
    removeInstruction(instructionGuid);
    handleNavigation();
  };

  const handleSaveInstruction = () => {
    const instructionData = {
      ...formData,
      isSystemInstruction: instructionType === 'system',
    };

    if (instructionToEdit) {
      updateInstruction(instructionData);
    } else {
      createNewInstruction(instructionData);
    }
    handleNavigation();
  };

  return (
    <Flex className={clsx(AppPagePadding, styles.container)}>
      <PageHeader
        title="Instruction"
        goBackText="Instructions"
        onClickGoBack={() => navigate('/instructions')}
      />
      <Flex flexDirection="column" gap="24">
        <Field label="Name" required>
          <Flex flexDirection="row" justifyContent="start" alignItems="center">
            <Input
              value={formData.title}
              placeholder="Instruction Name"
              className={styles.nameInput}
              onChange={e =>
                dispatch({ type: 'UPDATE_FIELD', field: 'title', value: e.target.value })
              }
            />
            <Switch
              checked={!formData.deactivated}
              size="md"
              onCheckedChange={checked =>
                dispatch({ type: 'UPDATE_FIELD', field: 'deactivated', value: !checked })
              }
            >
              Active
            </Switch>
          </Flex>
        </Field>
        <Field
          label="Core Instruction"
          info="Information and context for Opal to tailor its output. Include details like guidelines, response formats, and constraints"
        >
          <Textarea
            value={formData.instruction}
            className={styles.instructionInput}
            placeholder="Enter the core instruction"
            onChange={e =>
              dispatch({ type: 'UPDATE_FIELD', field: 'instruction', value: e.target.value })
            }
            resize="vertical"
          />
        </Field>
        <Field
          label="Where to use"
          info="Specifies a product and instance where this instruction applies. If none is selected, it will apply to all products."
        >
          <UserInstructionProductScope
            userInstruction={formData}
            onUpdate={userInstruction => dispatch({ type: 'SET_ALL', payload: userInstruction })}
          />
        </Field>
        <Field
          label="When to use"
          info="Specifies the exact conditions that will trigger this instruction, such as the user's intent, keywords, or phrases"
        >
          <Input
            value={formData.instruction_sementics}
            className={styles.instructionSementicsInput}
            placeholder='Specify when this instruction should apply, for example "when creating content"'
            onChange={e =>
              dispatch({
                type: 'UPDATE_FIELD',
                field: 'instruction_sementics',
                value: e.target.value,
              })
            }
          />
        </Field>
        <Separator className={styles.divider} />
        <Flex
          className={styles.footer}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            appearance="danger"
            onClick={() => handleDeleteInstruction(formData.instruction_guid)}
            disabled={!instructionToEdit}
          >
            Delete
          </Button>
          <Flex flexDirection="row" gap="16" alignItems="center">
            <Button
              appearance="default"
              onClick={() => {
                navigate('/instructions');
              }}
            >
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSaveInstruction}>
              Save
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
